/**
 * General File Service Types
 *
 * Types for file operations not scoped to a specific matter
 */

import { z } from 'zod';
import { acceptedMimeTypesFor } from '@/lib/constants/file-type-accept';

// Core Types

/**
 * File status
 */
export type FileStatus = 'uploaded' | 'processing' | 'available' | 'failed';

/**
 * Supported file types
 */
export type FileType =
  | 'death-certificate'
  | 'will'
  | 'codicil'
  | 'grant-of-representation'
  | 'primary-id'
  | 'secondary-id'
  | 'correspondence-attachment'
  | 'institution-form'
  | 'institution-form-template'
  | 'institution-form-signed'
  | 'letterhead'
  | 'handwritten-signature'
  | 'email-signature'
  | 'distribution-declaration'
  | 'matter-document'
  | 'suggestion-attachment';

/**
 * File entity
 */
export interface FileEntity {
  id: number;
  originalFilename: string;
  status: FileStatus;
  s3Key: string;
  imageS3Key?: string;
  mimeType: string;
  sizeBytes: number;
  fileType: string;
  extractedData?: Record<string, unknown>;
  isCertifiedCopy: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;

  // Relations
  matterId?: number;
  uploadedBy?: {
    id: number;
    email: string;
    firstName: string;
    lastName: string;
  };
}

// Request/Response Types

/**
 * Get presigned URL request
 */
export interface GetPresignedUrlRequest {
  fileType: FileType;
  matterId?: number;
}

/**
 * Presigned URL response
 */
export interface PresignedUrlResponse {
  presignedUrl: string;
  fileId: number;
}

/**
 * Upload complete request
 */
export interface UploadCompleteRequest {
  fileId: number;
  originalFilename: string;
}

/**
 * What `POST /files/:fileId/upload-complete` actually answers with
 * (`UploadCompleteResponseDto` backend-side) — an acknowledgement, NOT the file
 * row. Typing this as `FileEntity` is what made callers read `.id` off an
 * object that has none.
 */
export interface UploadCompleteResponse {
  fileId: number;
  fieldClassificationReport?: unknown;
}

/**
 * Update file request
 */
export interface UpdateFileRequest {
  isCertifiedCopy?: boolean;
}

/**
 * Download response
 */
export interface FileDownloadResponse {
  downloadUrl: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
}

// Validation Schemas

export const FileTypeSchema = z.enum([
  'death-certificate',
  'will',
  'codicil',
  'grant-of-representation',
  'primary-id',
  'secondary-id',
  'correspondence-attachment',
  'institution-form',
  'institution-form-template',
  'institution-form-signed',
  'letterhead',
  'handwritten-signature',
  'email-signature',
  'distribution-declaration',
  'matter-document',
  'suggestion-attachment'
]);

export const FileStatusSchema = z.enum([
  'uploaded',
  'processing',
  'available',
  'failed'
]);

export const GetPresignedUrlRequestSchema = z.object({
  fileType: FileTypeSchema,
  matterId: z.number().int().positive().optional()
});

export const UploadCompleteRequestSchema = z.object({
  fileId: z.number().int().positive(),
  originalFilename: z.string().min(1).max(255)
});

export const UpdateFileRequestSchema = z.object({
  isCertifiedCopy: z.boolean().optional()
});

// Action Result Types

export interface FileActionResult<T> {
  success: boolean;
  data?: T;
  error?: string;
  validationErrors?: Record<string, string[]>;
}

// File Type Metadata

export interface FileTypeMetadata {
  label: string;
  description: string;
  /**
   * What the destination backend file type accepts, read from `lib/constants/file-type-accept`.
   *
   * Optional because one registry key is not an upload at all: the email signature is authored
   * in the app and stored as HTML on the user record, so it has no backend file type and states
   * no format contract. `validateFile` refuses an upload against such a key rather than guessing.
   */
  acceptedMimeTypes?: string[];
  maxSizeBytes: number;
  requiresCertification: boolean;
  icon?: string;
}

/**
 * Per-file-type display metadata.
 *
 * **`acceptedMimeTypes` is not authored here.** It is read from
 * `lib/constants/file-type-accept.ts`, which derives it from the backend's own
 * `FILE_TYPES[slug].allowedMimeTypes` (VALE-1150), so this registry cannot advertise a format
 * the upload would then be rejected for. Until VALE-209 these lists were typed by hand and had
 * drifted badly: GIF, WEBP, CSV, RTF and ZIP were offered on document types the backend
 * accepts none of, and `letterhead` offered JPG/PNG where the backend takes PDF only.
 *
 * The labels, sizes, icons and descriptions ARE this file's own — only the format contract
 * belongs to the backend.
 */
export const FILE_TYPE_METADATA: Record<FileType, FileTypeMetadata> = {
  'death-certificate': {
    label: 'Death Certificate',
    description: 'Official death certificate from vital records',
    acceptedMimeTypes: acceptedMimeTypesFor('death-certificate'),
    maxSizeBytes: 20 * 1024 * 1024, // 20MB
    requiresCertification: true,
    icon: 'FileText'
  },
  will: {
    label: 'Will',
    description: 'Last will and testament',
    acceptedMimeTypes: acceptedMimeTypesFor('will'),
    maxSizeBytes: 50 * 1024 * 1024, // 50MB
    requiresCertification: true,
    icon: 'ScrollText'
  },
  codicil: {
    label: 'Codicil',
    description: 'A document that amends the will',
    acceptedMimeTypes: acceptedMimeTypesFor('codicil'),
    maxSizeBytes: 50 * 1024 * 1024, // 50MB
    requiresCertification: true,
    icon: 'ScrollText'
  },
  'grant-of-representation': {
    label: 'Grant of Representation',
    description: 'Court-issued grant of representation',
    acceptedMimeTypes: acceptedMimeTypesFor('grant-of-representation'),
    maxSizeBytes: 20 * 1024 * 1024, // 20MB
    requiresCertification: true,
    icon: 'Scale'
  },
  'primary-id': {
    label: 'Primary ID',
    description: 'Government-issued primary identification',
    acceptedMimeTypes: acceptedMimeTypesFor('primary-id'),
    maxSizeBytes: 20 * 1024 * 1024, // 20MB
    requiresCertification: true,
    icon: 'IdCard'
  },
  'secondary-id': {
    label: 'Secondary ID',
    description: 'Secondary identification document',
    acceptedMimeTypes: acceptedMimeTypesFor('secondary-id'),
    maxSizeBytes: 20 * 1024 * 1024, // 20MB
    requiresCertification: false,
    icon: 'IdCard'
  },
  'correspondence-attachment': {
    label: 'Correspondence Attachment',
    description: 'File attached to correspondence',
    acceptedMimeTypes: acceptedMimeTypesFor('correspondence-attachment'),
    maxSizeBytes: 25 * 1024 * 1024, // 25MB
    requiresCertification: false,
    icon: 'Paperclip'
  },
  /**
   * A display-only label covering both institution-form types; it takes the signed type's
   * contract, the broader of the two.
   */
  'institution-form': {
    label: 'Institution Form',
    description: 'Form from financial institution',
    acceptedMimeTypes: acceptedMimeTypesFor('institution-form-signed'),
    maxSizeBytes: 20 * 1024 * 1024, // 20MB
    requiresCertification: false,
    icon: 'FileText'
  },
  'institution-form-template': {
    label: 'Institution Form Template',
    description: 'Template for institution forms',
    acceptedMimeTypes: acceptedMimeTypesFor('institution-form-template'),
    maxSizeBytes: 20 * 1024 * 1024, // 20MB
    requiresCertification: false,
    icon: 'FileText'
  },
  'institution-form-signed': {
    label: 'Signed Institution Form',
    description: 'Signed institution form',
    acceptedMimeTypes: acceptedMimeTypesFor('institution-form-signed'),
    maxSizeBytes: 20 * 1024 * 1024, // 20MB
    requiresCertification: false,
    icon: 'FileCheck'
  },
  letterhead: {
    label: 'Letterhead',
    description: 'Law firm letterhead',
    acceptedMimeTypes: acceptedMimeTypesFor('letterhead'),
    maxSizeBytes: 20 * 1024 * 1024, // 20MB
    requiresCertification: false,
    icon: 'FileText'
  },
  'handwritten-signature': {
    label: 'Handwritten Signature',
    description: 'Handwritten signature image',
    acceptedMimeTypes: acceptedMimeTypesFor('handwritten-signature'),
    maxSizeBytes: 5 * 1024 * 1024, // 5MB
    requiresCertification: false,
    icon: 'PenTool'
  },
  'email-signature': {
    label: 'Email Signature',
    description: 'Email signature template',
    /**
     * Not an uploaded file type, and so it states no format contract at all (VALE-1150). The
     * email signature is authored in the app and stored as HTML on the user record: there is no
     * backend `FileTypeSlug`, no upload, and nothing to validate. It had carried a local
     * `['text/html', 'text/plain']` literal, which described no door anyone could open. It stays
     * in this registry only for its label and icon.
     */
    maxSizeBytes: 5 * 1024 * 1024, // 5MB
    requiresCertification: false,
    icon: 'Mail'
  },
  'distribution-declaration': {
    label: 'Distribution Declaration',
    description: "A beneficiary's distribution declaration document",
    acceptedMimeTypes: acceptedMimeTypesFor('distribution-declaration'),
    maxSizeBytes: 25 * 1024 * 1024, // 25MB
    requiresCertification: false,
    icon: 'file-signature'
  },
  /**
   * The document-type detector's fallback label (`lib/utils/file-type-detector.ts`) rather
   * than an upload type of its own — a file that lands here is stored as an
   * `additional-document`, which `DISPLAY_ONLY_ALIASES` records so the lookup resolves it.
   */
  'matter-document': {
    label: 'Matter Document',
    description: 'General document related to the matter',
    acceptedMimeTypes: acceptedMimeTypesFor('matter-document'),
    maxSizeBytes: 25 * 1024 * 1024, // 25MB
    requiresCertification: false,
    icon: 'File'
  },
  'suggestion-attachment': {
    label: 'Suggestion Attachment',
    description: 'File attached to a product suggestion',
    acceptedMimeTypes: acceptedMimeTypesFor('suggestion-attachment'),
    maxSizeBytes: 5 * 1024 * 1024, // 5MB
    requiresCertification: false,
    icon: 'MessageSquare'
  }
};
